// THEME CONFIGURATION
// Atlassian Design System inspired theme

// -------------------------- DESIGN TOKENS --------------------------

// Color Palette - Based on Atlassian Design System
const colors = {
  // Primary Colors
  primary: "#0052CC", // Bold blue for primary actions
  primaryHover: "#0065FF",
  primaryActive: "#0747A6",
  
  // Status Colors (for task states)
  success: "#00875A", // Green for "Done" state
  successHover: "#00A572",
  successLight: "#E3FCEF",
  
  warning: "#FFAB00", // Yellow for "In Progress" state  
  warningHover: "#FFC400",
  warningLight: "#FFF0B3",
  
  info: "#0065FF", // Blue for "To Do" state
  infoHover: "#2684FF",
  infoLight: "#DEEBFF",
  
  error: "#DE350B", // Red for errors and destructive actions
  errorHover: "#FF5630",
  errorLight: "#FFEBE6",
  
  // Neutral Colors
  neutral900: "#091E42", // Dark text (headings)
  neutral800: "#172B4D", // Body text
  neutral700: "#253858",
  neutral600: "#344563",
  neutral500: "#505F79", // Secondary text
  neutral400: "#5E6C84",
  neutral300: "#6B778C",
  neutral200: "#7A869A",
  neutral100: "#8993A4",
  neutral90: "#97A0AF",
  neutral80: "#A5ADBA",
  neutral70: "#B3BAC5",
  neutral60: "#C1C7D0",
  neutral50: "#DFE1E6", // Borders
  neutral40: "#EBECF0",
  neutral30: "#F4F5F7", // Backgrounds
  neutral20: "#FAFBFC",
  neutral10: "#FFFFFF",
  
  // Background Colors
  background: "#FAFBFC",
  backgroundCard: "#FFFFFF",
  backgroundHover: "#F4F5F7",
  backgroundActive: "#EBECF0",
};

// Typography
const typography = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  fontSizeBase: 14,
  fontSizes: {
    h1: 35,
    h2: 29,
    h3: 24,
    h4: 20,
    h5: 16,
    base: 14,
    small: 12,
    tiny: 11,
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeights: {
    tight: 1.2,
    base: 1.4286, // 20px for 14px font
    relaxed: 1.5,
  },
};

// Spacing (4px grid system)
const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  base: 16,
  lg: 24,
  xl: 32,
  xxl: 40,
  xxxl: 48,
};

// Border Radius
const borderRadius = {
  subtle: 3,
  base: 4,
  round: 50,
};

// Shadows
const shadows = {
  card: "0 1px 1px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)",
  cardHover: "0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)",
  modal: "0 0 0 1px rgba(9, 30, 66, 0.08), 0 2px 4px 1px rgba(9, 30, 66, 0.08)",
  dropdown: "0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)",
};

// -------------------------- THEME ADMIN (Original) --------------------------

const theme1 = {
  components: {
    Menu: {
      itemBg: "rgba(255,255,255,0)",
      itemSelectedBg: "transparent",
      itemSelectedColor: "rgb(0,0,0)",
      subMenuItemBg: "rgba(0,0,0,0)",
      colorText: "rgba(0,0,0,0.65)",
      colorSplit: "rgba(5,5,5,0)",
      itemColor: "rgba(0,0,0,0.47)",
    },
  },
};

// -------------------------- ATLASSIAN THEME --------------------------

const atlassianTheme = {
  token: {
    // Colors
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,
    
    // Typography
    fontFamily: typography.fontFamily,
    fontSize: typography.fontSizeBase,
    fontSizeHeading1: typography.fontSizes.h1,
    fontSizeHeading2: typography.fontSizes.h2,
    fontSizeHeading3: typography.fontSizes.h3,
    fontSizeHeading4: typography.fontSizes.h4,
    fontSizeHeading5: typography.fontSizes.h5,
    fontSizeSM: typography.fontSizes.small,
    
    // Border Radius
    borderRadius: borderRadius.base,
    borderRadiusSM: borderRadius.subtle,
    borderRadiusLG: borderRadius.base,
    
    // Spacing
    padding: spacing.base,
    paddingSM: spacing.sm,
    paddingLG: spacing.lg,
    paddingXS: spacing.xs,
    margin: spacing.base,
    marginSM: spacing.sm,
    marginLG: spacing.lg,
    marginXS: spacing.xs,
    
    // Text Colors
    colorText: colors.neutral800,
    colorTextSecondary: colors.neutral500,
    colorTextTertiary: colors.neutral400,
    colorTextQuaternary: colors.neutral300,
    
    // Background Colors
    colorBgContainer: colors.backgroundCard,
    colorBgElevated: colors.neutral10,
    colorBgLayout: colors.background,
    colorBgSpotlight: colors.backgroundHover,
    
    // Border Colors
    colorBorder: colors.neutral50,
    colorBorderSecondary: colors.neutral40,
    
    // Shadows
    boxShadow: shadows.card,
    boxShadowSecondary: shadows.dropdown,
    
    // Line Heights
    lineHeight: typography.lineHeights.base,
    lineHeightHeading1: typography.lineHeights.tight,
    lineHeightHeading2: typography.lineHeights.tight,
    lineHeightHeading3: typography.lineHeights.tight,
    lineHeightHeading4: typography.lineHeights.tight,
    lineHeightHeading5: typography.lineHeights.tight,
  },
  
  components: {
    // Button
    Button: {
      primaryShadow: "none",
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      fontWeight: typography.fontWeights.medium,
      borderRadius: borderRadius.subtle,
      paddingContentHorizontal: spacing.md,
    },
    
    // Input
    Input: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      borderRadius: borderRadius.subtle,
      paddingBlock: 6,
      paddingInline: spacing.sm,
      hoverBorderColor: colors.neutral300,
      activeBorderColor: colors.primary,
      activeShadow: `0 0 0 2px ${colors.infoLight}`,
    },
    
    // Select
    Select: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      borderRadius: borderRadius.subtle,
      optionPadding: `${spacing.xs}px ${spacing.sm}px`,
    },
    
    // Card
    Card: {
      borderRadiusLG: borderRadius.base,
      boxShadow: shadows.card,
      boxShadowHover: shadows.cardHover,
      paddingLG: spacing.base,
    },
    
    // Modal
    Modal: {
      borderRadiusLG: borderRadius.base,
      boxShadow: shadows.modal,
      headerBg: colors.backgroundCard,
      contentBg: colors.backgroundCard,
      paddingContentHorizontalLG: spacing.lg,
    },
    
    // Table
    Table: {
      borderRadius: borderRadius.base,
      headerBg: colors.backgroundHover,
      headerColor: colors.neutral800,
      rowHoverBg: colors.backgroundHover,
      borderColor: colors.neutral50,
      cellPaddingBlock: spacing.md,
      cellPaddingInline: spacing.base,
    },
    
    // Menu
    Menu: {
      itemBg: "transparent",
      itemSelectedBg: colors.backgroundActive,
      itemSelectedColor: colors.primary,
      itemHoverBg: colors.backgroundHover,
      itemColor: colors.neutral800,
      itemBorderRadius: borderRadius.subtle,
      itemPaddingInline: spacing.md,
      itemMarginBlock: 2,
      iconSize: 16,
    },
    
    // Tabs
    Tabs: {
      itemColor: colors.neutral500,
      itemSelectedColor: colors.primary,
      itemHoverColor: colors.neutral800,
      inkBarColor: colors.primary,
      cardBg: colors.backgroundCard,
    },
    
    // Badge
    Badge: {
      fontSize: typography.fontSizes.small,
      fontWeight: typography.fontWeights.semibold,
      textFontSize: typography.fontSizes.tiny,
    },
    
    // Tag
    Tag: {
      borderRadiusSM: borderRadius.subtle,
      defaultBg: colors.neutral40,
      defaultColor: colors.neutral800,
      fontSize: typography.fontSizes.small,
    },
    
    // Alert
    Alert: {
      borderRadiusLG: borderRadius.base,
      paddingContentHorizontalLG: spacing.base,
      paddingContentVerticalLG: spacing.md,
    },
    
    // Message
    Message: {
      contentBg: colors.backgroundCard,
      contentPadding: `${spacing.sm}px ${spacing.base}px`,
    },
    
    // Notification
    Notification: {
      paddingContentHorizontal: spacing.base,
      paddingContentVertical: spacing.base,
      borderRadiusLG: borderRadius.base,
    },
    
    // Tooltip
    Tooltip: {
      colorBgSpotlight: colors.neutral900,
      borderRadius: borderRadius.subtle,
      paddingSM: spacing.xs,
    },
    
    // Popover
    Popover: {
      borderRadiusLG: borderRadius.base,
      boxShadow: shadows.dropdown,
    },
    
    // Drawer
    Drawer: {
      paddingLG: spacing.lg,
      borderRadiusLG: 0,
    },
    
    // Progress
    Progress: {
      defaultColor: colors.primary,
      remainingColor: colors.neutral40,
      circleTextColor: colors.neutral800,
    },
    
    // Skeleton
    Skeleton: {
      borderRadiusSM: borderRadius.subtle,
      gradientFromColor: colors.neutral40,
      gradientToColor: colors.neutral30,
    },
    
    // Avatar
    Avatar: {
      borderRadius: borderRadius.round,
      colorTextPlaceholder: colors.neutral10,
    },
    
    // Breadcrumb
    Breadcrumb: {
      itemColor: colors.neutral500,
      lastItemColor: colors.neutral800,
      linkColor: colors.neutral500,
      linkHoverColor: colors.primary,
      separatorColor: colors.neutral400,
    },
    
    // Pagination
    Pagination: {
      borderRadius: borderRadius.subtle,
      itemBg: "transparent",
      itemSize: 32,
    },
    
    // Steps
    Steps: {
      iconSize: 32,
      iconSizeSM: 24,
      dotSize: 8,
    },
    
    // Form
    Form: {
      labelFontSize: typography.fontSizeBase,
      labelColor: colors.neutral800,
      labelHeight: 32,
      verticalLabelPadding: `0 0 ${spacing.xs}px`,
      itemMarginBottom: spacing.lg,
    },
    
    // DatePicker
    DatePicker: {
      controlHeight: 32,
      controlHeightLG: 40,
      controlHeightSM: 24,
      borderRadius: borderRadius.subtle,
      cellHoverBg: colors.backgroundHover,
      cellActiveWithRangeBg: colors.infoLight,
    },
    
    // Upload
    Upload: {
      actionsColor: colors.neutral500,
    },
    
    // Switch
    Switch: {
      handleSize: 18,
      innerMinMargin: 6,
      innerMaxMargin: 24,
      trackHeight: 22,
      trackMinWidth: 44,
      trackPadding: 2,
    },
    
    // Checkbox & Radio
    Checkbox: {
      borderRadiusSM: borderRadius.subtle,
      size: 16,
    },
    
    Radio: {
      size: 16,
      dotSize: 8,
    },
    
    // Rate
    Rate: {
      starSize: 20,
      starColor: colors.warning,
    },
    
    // Slider
    Slider: {
      railSize: 4,
      handleSize: 14,
      handleSizeHover: 16,
      dotSize: 8,
      railBg: colors.neutral50,
      trackBg: colors.primary,
    },
    
    // Timeline
    Timeline: {
      tailColor: colors.neutral50,
      dotBg: colors.backgroundCard,
      dotBorderWidth: 2,
    },
    
    // Divider
    Divider: {
      colorSplit: colors.neutral50,
    },
    
    // List
    List: {
      itemPadding: `${spacing.md}px 0`,
      itemPaddingSM: `${spacing.sm}px ${spacing.base}px`,
      itemPaddingLG: `${spacing.base}px ${spacing.lg}px`,
    },
    
    // Dropdown
    Dropdown: {
      paddingBlock: spacing.xs,
      borderRadiusLG: borderRadius.base,
      boxShadowSecondary: shadows.dropdown,
    },
    
    // Cascader  
    Cascader: {
      controlHeight: 32,
      controlItemWidth: 200,
      borderRadius: borderRadius.subtle,
    },
    
    // Tree
    Tree: {
      titleHeight: 32,
      nodeHoverBg: colors.backgroundHover,
      nodeSelectedBg: colors.backgroundActive,
    },
    
    // Transfer
    Transfer: {
      headerHeight: 40,
      itemHeight: 32,
      itemHoverBg: colors.backgroundHover,
      itemPaddingBlock: spacing.xs,
    },
    
    // Calendar
    Calendar: {
      fullBg: colors.backgroundCard,
      fullPanelBg: colors.backgroundCard,
      itemActiveBg: colors.infoLight,
    },
  },
};

// -------------------------- EXPORTS --------------------------

export const getThemes = () => {
  return {
    theme1,
    atlassianTheme,
  };
};

export const getDesignTokens = () => {
  return {
    colors,
    typography,
    spacing,
    borderRadius,
    shadows,
  };
};
